import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["canvas", "loading", "nodeModal"];
  static values = { id: String };

  connect() {
    this.fetchMindMapData();
    this.currentNode = null;
    this.width = this.canvasTarget.clientWidth;
    this.height = this.canvasTarget.clientHeight;
  }

  fetchMindMapData() {
    fetch(`/admin/education/mind_maps/${this.idValue}.json`)
      .then((response) => response.json())
      .then((data) => {
        this.loadingTarget.classList.add("hidden");
        this.renderBasicMindMap(data);
      })
      .catch((error) => {
        console.error("Error fetching mind map data:", error);
        this.loadingTarget.innerHTML = "Error loading mind map data";
      });
  }

  renderBasicMindMap(data) {
    // Create a simple HTML/CSS based mind map
    const container = document.createElement("div");
    container.className =
      "basic-mind-map relative w-full h-full overflow-hidden";

    // Create nodes
    data.nodes.forEach((node) => {
      const nodeElement = document.createElement("div");
      nodeElement.className =
        "absolute p-2 rounded-lg shadow-md border transition-all";
      nodeElement.setAttribute("data-node-id", node.id);
      nodeElement.style.left = `${node.position_x + this.width / 2}px`;
      nodeElement.style.top = `${node.position_y + this.height / 2}px`;
      nodeElement.style.backgroundColor = this.getNodeColor(node.node_type);
      nodeElement.style.borderColor = "rgba(0,0,0,0.1)";
      nodeElement.style.color = "white";
      nodeElement.style.maxWidth = "150px";
      nodeElement.style.zIndex = "10";

      // Add content
      nodeElement.innerHTML = `
        <h4 class="text-sm font-medium truncate">${node.title}</h4>
        <p class="text-xs opacity-80 truncate">${node.content || ""}</p>
      `;

      // Add event listeners
      nodeElement.addEventListener("click", (e) =>
        this.handleNodeClick(e, node)
      );

      // Make draggable
      this.makeNodeDraggable(nodeElement);

      container.appendChild(nodeElement);
    });

    // Draw simple connection lines using CSS
    data.connections.forEach((conn) => {
      const sourceNode = data.nodes.find((n) => n.id === conn.source_node_id);
      const targetNode = data.nodes.find((n) => n.id === conn.target_node_id);

      if (sourceNode && targetNode) {
        const line = document.createElement("div");
        line.className =
          "absolute border-t border-gray-400 transform origin-left";
        line.style.zIndex = "5";

        this.positionConnectionLine(line, sourceNode, targetNode);
        container.appendChild(line);
      }
    });

    this.canvasTarget.appendChild(container);
  }

  positionConnectionLine(line, source, target) {
    // Calculate positions
    const sourceX = source.position_x + this.width / 2;
    const sourceY = source.position_y + this.height / 2;
    const targetX = target.position_x + this.width / 2;
    const targetY = target.position_y + this.height / 2;

    // Calculate length and angle
    const length = Math.sqrt(
      Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2)
    );
    const angle =
      (Math.atan2(targetY - sourceY, targetX - sourceX) * 180) / Math.PI;

    // Position the line
    line.style.width = `${length}px`;
    line.style.left = `${sourceX}px`;
    line.style.top = `${sourceY}px`;
    line.style.transform = `rotate(${angle}deg)`;
  }

  makeNodeDraggable(element) {
    let pos1 = 0,
      pos2 = 0,
      pos3 = 0,
      pos4 = 0;

    element.onmousedown = dragMouseDown;

    const that = this;

    function dragMouseDown(e) {
      e.preventDefault();
      // Get the mouse cursor position at startup
      pos3 = e.clientX;
      pos4 = e.clientY;
      document.onmouseup = closeDragElement;
      // Call a function whenever the cursor moves
      document.onmousemove = elementDrag;
    }

    function elementDrag(e) {
      e.preventDefault();
      // Calculate the new cursor position
      pos1 = pos3 - e.clientX;
      pos2 = pos4 - e.clientY;
      pos3 = e.clientX;
      pos4 = e.clientY;
      // Set the element's new position
      element.style.top = element.offsetTop - pos2 + "px";
      element.style.left = element.offsetLeft - pos1 + "px";
    }

    function closeDragElement() {
      // Stop moving when mouse button is released
      document.onmouseup = null;
      document.onmousemove = null;

      // Update node position in database
      const nodeId = element.getAttribute("data-node-id");
      const newPosX = parseInt(element.style.left) - that.width / 2;
      const newPosY = parseInt(element.style.top) - that.height / 2;

      that.updateNodePosition(nodeId, newPosX, newPosY);
    }
  }

  updateNodePosition(nodeId, x, y) {
    const token = document.querySelector('meta[name="csrf-token"]').content;

    fetch(`/admin/education/mind_maps/${this.idValue}/nodes/${nodeId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
      },
      body: JSON.stringify({
        node: {
          position_x: x,
          position_y: y,
        },
      }),
    }).catch((error) => {
      console.error("Error updating node position:", error);
    });
  }

  getNodeColor(type) {
    // These should match the colors in our view
    switch (type) {
      case "main":
        return "#4f46e5";
      case "lesson":
        return "#0ea5e9";
      case "module":
        return "#8b5cf6";
      case "course":
        return "#d946ef";
      case "key_point":
        return "#10b981";
      case "example":
        return "#f59e0b";
      case "definition":
        return "#64748b";
      case "question":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  }

  // Existing methods for modal and node management...
  handleNodeClick(event, node) {
    console.log("Node clicked:", node);
  }

  zoomIn(event) {
    const container = this.canvasTarget.querySelector(".basic-mind-map");
    container.style.transform = "scale(1.2)";
  }

  zoomOut(event) {
    const container = this.canvasTarget.querySelector(".basic-mind-map");
    container.style.transform = "scale(0.8)";
  }

  resetZoom(event) {
    const container = this.canvasTarget.querySelector(".basic-mind-map");
    container.style.transform = "scale(1)";
  }

  // Keep your existing methods for adding/editing/deleting nodes
  addNode(event) {
    document.getElementById("modal-title").textContent = "Add New Node";
    document.getElementById("node-title").value = "";
    document.getElementById("node-content").value = "";
    document.getElementById("node-type").value = "key_point";

    this.currentNode = null;
    this.nodeModalTarget.classList.remove("hidden");
  }

  editNode(event) {
    const nodeId = event.currentTarget.dataset.nodeId;

    // Fetch node data
    fetch(`/admin/education/mind_maps/${this.idValue}/nodes/${nodeId}.json`)
      .then((response) => response.json())
      .then((node) => {
        document.getElementById("modal-title").textContent = "Edit Node";
        document.getElementById("node-title").value = node.title;
        document.getElementById("node-content").value = node.content;
        document.getElementById("node-type").value = node.node_type;

        this.currentNode = node;
        this.nodeModalTarget.classList.remove("hidden");
      });
  }

  saveNode(event) {
    const title = document.getElementById("node-title").value;
    const content = document.getElementById("node-content").value;
    const nodeType = document.getElementById("node-type").value;

    if (!title) {
      alert("Title is required");
      return;
    }

    let url, method;

    if (this.currentNode) {
      // Update existing node
      url = `/admin/education/mind_maps/${this.idValue}/nodes/${this.currentNode.id}`;
      method = "PATCH";
    } else {
      // Create new node
      url = `/admin/education/mind_maps/${this.idValue}/nodes`;
      method = "POST";
    }

    const token = document.querySelector('meta[name="csrf-token"]').content;

    fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
      },
      body: JSON.stringify({
        node: {
          title: title,
          content: content,
          node_type: nodeType,
          position_x: this.width / 2 + Math.random() * 100 - 50,
          position_y: this.height / 2 + Math.random() * 100 - 50,
        },
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        this.closeModal();
        // Refresh the mind map
        location.reload();
      })
      .catch((error) => {
        console.error("Error saving node:", error);
        alert("Error saving node. Please try again.");
      });
  }

  deleteNode(event) {
    if (!confirm("Are you sure you want to delete this node?")) return;

    const nodeId = event.currentTarget.dataset.nodeId;
    const token = document.querySelector('meta[name="csrf-token"]').content;

    fetch(`/admin/education/mind_maps/${this.idValue}/nodes/${nodeId}`, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": token,
      },
    })
      .then(() => {
        // Refresh the mind map
        location.reload();
      })
      .catch((error) => {
        console.error("Error deleting node:", error);
        alert("Error deleting node. Please try again.");
      });
  }

  closeModal(event) {
    this.nodeModalTarget.classList.add("hidden");
  }

  addConnection(event) {
    alert("Select two nodes to connect. This feature is under development.");
  }
}
